import React, { useEffect, useState } from "react";

import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  Badge,
  HStack,
  Spinner,
  Flex,
  IconButton,
  Divider,
  Tooltip,
  Center,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FaArrowLeft,
  FaChartBar,
  FaInfoCircle,
  FaFolder,
  FaCircle,
  FaSearch,
  FaUsers,
} from "react-icons/fa";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase/firebase";

import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import { IconWrapper } from "../../../components/IconWrapper";
import RiskManagementChart from "../../Gantt/RiskManagementChart";
import StrikeUser from "./StrikeUser";
import CustomDropdown from "../../../components/CustomDropdown";
import { formatDateDDMMYYYY } from "../../../utils/dateUtils";

Chart.register(...registerables);

interface TaskData {
  id: string;
  name: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart?: string;
  actualEnd?: string;
  status: string;
  assignedUsers?: string[];
  progress: number;
  subtasks?: TaskData[];
  risk?: string;
}

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string;
  assignedProjects?: string[];
  assignedTasks?: { id: string; name: string }[];
  organizationId: string;
};

interface Task {
  id: string;
  title: string;
  description: string;
  data?: TaskData[];
}

type TaskStatus = "Not-started" | "In-progress" | "completed" | "delay";
type StatusKey = "Not-started" | "In-progress" | "completed" | "delay";
interface ChannelDetailsProps {
  organizationId: string;
  projectId: string;
  channelId: string;
  channelName: string;
  onBack: () => void;
  color: string;
  bg: string;
  users: User[];
  isAdmin: boolean;
  currentUser?: any;
}

const ChannelDetails: React.FC<ChannelDetailsProps> = ({
  organizationId,
  projectId,
  channelId,
  channelName,
  bg: _bg,
  color: _color,
  onBack,
  users,
  isAdmin,
  currentUser,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [view, setView] = useState<"details" | "chart">("details");
  const [searchQuery, setSearchQuery] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [assigneeList, setAssigneeList] = useState<string[]>([]);

  // Filter tasks based on search query
  const filteredTasks = tasks.filter((task) =>
    task.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const legendUp: React.CSSProperties = { bottom: 30, fontSize: "12px" }; // tweak 8–20px to taste



  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const taskRef = collection(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks`
        );
        const taskSnapshot = await getDocs(taskRef);

        const tasksData = taskSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Task[];

        const isStrictAdmin = currentUser?.permissions?.toLowerCase() === "admin";
        const visibleTasks = isStrictAdmin
          ? tasksData
          : tasksData.filter((task) => {
              const assignedTasks = currentUser?.assignedTasks || [];
              return assignedTasks.some(
                (at: any) => at.taskId === task.id && at.channelId === channelId && at.projectId === projectId
              );
            });

        setTasks(visibleTasks);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [organizationId, projectId, channelId]);

  // Total Data Count Across All Tasks
  const totalTaskCount = tasks.length;

  const progressSummary: Record<string, number> = {
    "Not-started": 0,
    "In-progress": 0,
    completed: 0,
    delay: 0,
  };

  tasks.forEach((task) => {
    task.data?.forEach((entry) => {
      // Determine the normalized key
      let key = entry.status;
      if (entry.status === "Started" || entry.status === "In-progress") {
        key = "In-progress";
      } else if (entry.status.toLowerCase() === "completed") {
        key = "completed";
      }

      // Increment if the key exists
      if (progressSummary[key] !== undefined) {
        progressSummary[key]++;
      }

      entry.subtasks?.forEach((sub) => {
        let subKey = sub.status;
        if (sub.status === "Started" || sub.status === "In-progress") {
          subKey = "In-progress";
        } else if (sub.status.toLowerCase() === "completed") {
          subKey = "completed";
        }
        if (progressSummary[subKey] !== undefined) {
          progressSummary[subKey]++;
        }
      });
    });
  });

  const progressChartData = Object.entries(progressSummary).map(
    ([name, value]) => ({ name, value })
  );

  const getChartData = (task: Task) => {
    const statusCounts: Record<TaskStatus, number> = {
      "Not-started": 0,
      "In-progress": 0,
      completed: 0,
      delay: 0,
    };

    task.data?.forEach((item) => {
      const status = item.status as TaskStatus;
      if (status in statusCounts) {
        statusCounts[status]++;
      }

      // Include subtasks
      item.subtasks?.forEach((sub) => {
        const subStatus = sub.status as TaskStatus;
        if (subStatus in statusCounts) {
          statusCounts[subStatus]++;
        }
      });
    });

    return {
      labels: ["Not-started", "In-progress", "completed", "delay"],
      datasets: [
        {
          label: "Task Status Overview",
          data: [
            statusCounts["Not-started"],
            statusCounts["In-progress"],
            statusCounts["completed"],
            statusCounts["delay"],
          ],
          backgroundColor: ["#FEB2B2", "#90CDF4", "#9AE6B4", "#FAF089"],

          borderRadius: 8,
        },
      ],
    };
  };

  const statusSummary = {
    "Not-started": 0,
    "In-progress": 0,
    completed: 0,
    delay: 0,
  };

  const flatStatuses: string[] = [];

  tasks.forEach((task) => {
    task.data?.forEach((entry) => {
      if (entry.status) flatStatuses.push(entry.status);
      entry.subtasks?.forEach((sub) => {
        if (sub.status) flatStatuses.push(sub.status);
      });
    });
  });

  flatStatuses.forEach((status) => {
    // Normalize into unified buckets
    let key = status;
    if (status === "Started" || status === "In-progress") {
      key = "In-progress";
    } else if (status.toLowerCase() === "completed") {
      key = "completed";
    }

    // Increment the corresponding bucket if it exists
    if (key in statusSummary) {
      statusSummary[key as keyof typeof statusSummary]++;
    }
  });

  const pieChartData = Object.entries(statusSummary).map(([name, value]) => ({
    name,
    value,
  }));

  let assigned = 0;
  let unassigned = 0;

  tasks.forEach((task) => {
    task.data?.forEach((entry) => {
      // Top-level task
      if (entry.assignedUsers && entry.assignedUsers.length > 0) {
        assigned++;
      } else {
        unassigned++;
      }

      // Subtasks
      entry.subtasks?.forEach((sub) => {
        if (sub.assignedUsers && sub.assignedUsers.length > 0) {
          assigned++;
        } else {
          unassigned++;
        }
      });
    });
  });

  const assignmentChartData = [
    { name: "Assigned", value: assigned },
    { name: "Unassigned", value: unassigned },
  ];

  const normalizeStatus = (raw: string): StatusKey => {
    if (raw === "Started" || raw === "In-progress") {
      return "In-progress";
    }
    if (raw.toLowerCase() === "completed") {
      return "completed";
    }
    // Fallback to exact matching for "Not-started" or "delay"
    return raw as StatusKey;
  };

  const getStatusCount = (status: StatusKey): number => {
    return (
      selectedTask?.data?.reduce((count, entry) => {
        // Normalize the entry status
        const entryKey = entry.status ? normalizeStatus(entry.status) : null;
        let total = entryKey === status ? 1 : 0;

        // Normalize each subtask status and accumulate
        const subCount = entry.subtasks
          ? entry.subtasks.reduce((subSum, sub) => {
            const subKey = sub.status ? normalizeStatus(sub.status) : null;
            return subSum + (subKey === status ? 1 : 0);
          }, 0)
          : 0;

        return count + total + subCount;
      }, 0) || 0
    );
  };

  // assigned user names display (map ids → users, filter out missing)


  const bg = useColorModeValue("gray.50", "#14161B");
  const color = useColorModeValue("gray.800", "white");
  // const legendUp = { marginBottom: "10px" }; 

  // Extracted hooks
  const modalBg = useColorModeValue("white", "#14161B");

  const modalBorderColor = useColorModeValue("#E8C16C", "rgba(255,255,255,0.18)");
  const modalCloseColor = useColorModeValue("#1A1C1F", "#E2E8F0");
  const modalTextColor = useColorModeValue("#1A202C", "#E2E8F0");
  const modalDividerColor = useColorModeValue("gray.200", "rgba(255,255,255,0.18)"); // reused for list/table borders
  const listHoverBg = useColorModeValue("yellow.50", "#1E2228");
  const gray500 = useColorModeValue("text-gray-500", "text-gray-300");
  const divideColor = useColorModeValue("divide-gray-200", "divide-[rgba(255,255,255,0.18)]");
  const tableHeaderBg = useColorModeValue("gray.100", "#1A1C1F");
  const inputBg = useColorModeValue("white", "#1A1C1F");
  const inputTextColor = useColorModeValue("#1A202C", "#E2E8F0");
  const subTaskHoverBg = useColorModeValue("yellow.50", "#1E2228");
  const cardBorderColor = useColorModeValue("gray.200", "rgba(255,255,255,0.18)");
  const taskListBg = useColorModeValue("#F8FAFC", "#14161B");
  const taskListBorderColor = useColorModeValue("#E8E8E8", "rgba(255,255,255,0.18)");

  // Extracted constants for conditional/loop usage
  const sidebarBg = useColorModeValue("#F8FAFC", "#14161B");
  const sidebarBorderColor = useColorModeValue("#E8E8E8", "rgba(255,255,255,0.18)");
  const badgeGoldColor = useColorModeValue("#B59143", "#FBD282");
  const badgeGoldBg = useColorModeValue("#FFF1CC", "transparent");
  const badgeGoldBorder = useColorModeValue("#E8C16C", "#B59143");
  const searchInputBg = useColorModeValue("white", "#1A1C1F");
  const searchInputBorder = useColorModeValue("#E8C16C", "rgba(255,255,255,0.18)");
  const searchInputHoverBorder = useColorModeValue("#B59143", "#B59143");
  const isDark = useColorModeValue(false, true);
  const taskItemHoverBg = useColorModeValue("#FFF7E6", "#2A3441");
  const taskItemHoverBorder = useColorModeValue("#E8C16C", "#B59143");
  const taskItemSubTextColor = useColorModeValue("gray.600", "#C3C9D3");
  const summaryBoxBg = useColorModeValue("linear-gradient(90deg,rgba(181,145,67,0.12),rgba(181,145,67,0))", "#14161B");
  const summaryBoxBorder = useColorModeValue("#E8E8E8", "rgba(255,255,255,0.18)");
  const boxBorderColor = useColorModeValue("gray.300", "rgba(255,255,255,0.18)");
  const iconColor = useColorModeValue("text-gray-400", "text-[#C3C9D3]");
  const chartTextColor = useColorModeValue("gray.500", "gray.300");
  const descTextColor = useColorModeValue("gray.600", "#C3C9D3");
  const cardBgCommon = useColorModeValue("white", "#14161B");
  const riskBg = useColorModeValue("white", bg);

  // Chart Colors
  const COLORS: Record<string, string> = {
    "Not-started": "#CBD5E0",
    "In-progress": "#3182CE",
    Completed: "#38A169",
    delay: "#E53E3E",
  };

  const selectedStatusData: { name: StatusKey; value: number }[] = [
    { name: "Not-started", value: getStatusCount("Not-started") },
    { name: "In-progress", value: getStatusCount("In-progress") },
    { name: "completed", value: getStatusCount("completed") },
    { name: "delay", value: getStatusCount("delay") },
  ];

  // Helper to map user IDs to display names for table chips
  const getAssigneeNames = (ids?: string[]): string[] => {
    if (!ids || ids.length === 0) return [];
    return ids
      .map((id) => users.find((u) => u.id === id))
      .filter((u): u is User => Boolean(u))
      .map((u) => `${u.firstName} ${u.lastName || ""} `.trim());
  };

  // State for filtering and searching task entries
  const [entrySearchQuery, setEntrySearchQuery] = useState("");
  const [entryStatusFilter, setEntryStatusFilter] = useState<StatusKey | "All">(
    "All"
  );

  // Filter task entries based on search and status
  const filteredEntries =
    selectedTask?.data?.filter((entry) => {
      // Check if the parent entry matches the search query or status filter
      const parentMatchesSearch =
        entry.name.toLowerCase().includes(entrySearchQuery.toLowerCase()) ||
        entrySearchQuery === "";
      const parentMatchesStatus =
        entryStatusFilter === "All" ||
        normalizeStatus(entry.status) === entryStatusFilter;

      // Check if any subtask matches the search query or status filter
      const anySubtaskMatches =
        entry.subtasks?.some(
          (sub) =>
            sub.name.toLowerCase().includes(entrySearchQuery.toLowerCase()) &&
            (entryStatusFilter === "All" ||
              normalizeStatus(sub.status) === entryStatusFilter)
        ) || false;

      // Show the parent entry if it matches the filter itself, OR if any of its subtasks match the filter
      return (parentMatchesSearch && parentMatchesStatus) || anySubtaskMatches;
    }) || [];

  // Calculate risk counts for this channel
  let lowRisk = 0,
    mediumRisk = 0,
    highRisk = 0;

  tasks.forEach((task) => {
    task.data?.forEach((entry) => {
      if (entry.risk) {
        if (entry.risk.toLowerCase() === "low") lowRisk++;
        else if (entry.risk.toLowerCase() === "medium") mediumRisk++;
        else if (entry.risk.toLowerCase() === "high") highRisk++;
      }
      entry.subtasks?.forEach((sub) => {
        if (sub.risk) {
          if (sub.risk.toLowerCase() === "low") lowRisk++;
          else if (sub.risk.toLowerCase() === "medium") mediumRisk++;
          else if (sub.risk.toLowerCase() === "high") highRisk++;
        }
      });
    });
  });

  // ───────────────────────────────────────────────────────────────────────
  // ▶ STEP 1: Compute risk counts just for the SELECTED task (not the whole channel)
  // ───────────────────────────────────────────────────────────────────────
  const getSelectedTaskRiskData = () => {
    // If no task is selected, return zeros
    if (!selectedTask?.data) {
      return [
        { name: "High", value: 0 },
        { name: "Medium", value: 0 },
        { name: "Low", value: 0 },
      ];
    }

    let high = 0,
      medium = 0,
      low = 0;

    // Iterate through each entry (and its subtasks) in the selectedTask
    selectedTask.data.forEach((entry) => {
      if (entry.risk) {
        const riskValue = entry.risk.toLowerCase();
        if (riskValue === "high") high++;
        else if (riskValue === "medium") medium++;
        else if (riskValue === "low") low++;
      }
      entry.subtasks?.forEach((sub) => {
        if (sub.risk) {
          const subRiskValue = sub.risk.toLowerCase();
          if (subRiskValue === "high") high++;
          else if (subRiskValue === "medium") medium++;
          else if (subRiskValue === "low") low++;
        }
      });
    });

    return [
      { name: "High", value: high },
      { name: "Medium", value: medium },
      { name: "Low", value: low },
    ];
  };

  // Calculate the height for the task list section
  const getTaskListHeight = () => {
    const headerHeight = 80;
    const taskItemHeight = 90;
    const padding = 32;

    const maxTasks = 4;
    return (
      headerHeight + maxTasks * taskItemHeight + padding
    );
  };

  // Calculate the height for the New Section to match task list exactly (excluding pagination)
  const getNewSectionHeight = () => {
    const headerHeight = 192; // Approximate height for title and search
    const taskItemHeight = 90; // Approximate height per task item
    const padding = 32; // Total padding

    // Calculate the height for 4 tasks (maximum height) - this should be the consistent height
    const consistentHeight =
      headerHeight + 4 * taskItemHeight + 3 * 12 + padding - 236;

    // Always return the consistent height regardless of visible tasks
    return consistentHeight;
  };

  return (
    <Box px={5} bg={bg} borderRadius="lg" overflow="hidden">
      {/* Channel Name and Back Button at the very top */}
      <HStack justify="space-between" w="100%" px={2} mb={4}>
        <Heading
          fontSize={{ base: "md", md: "lg" }}
          color={color}
          noOfLines={1}
        >
          {channelName}
        </Heading>
        <Button
          leftIcon={<IconWrapper icon={FaArrowLeft} size={16} />}
          onClick={onBack}
          variant="gold"
          size="sm"
        >
          Back
        </Button>
      </HStack>

      {loading ? (
        <Flex align="center" justify="center" height="250px" width={"100%"}>
          <Spinner size="lg" color="purple.500" thickness="4px" speed="0.65s" />
        </Flex>
      ) : tasks.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          height="250px"
          bg={bg}
          borderColor="gray.300"
          p={6}
        >
          <Heading size="sm" mb={2} color={color}>
            No Tasks Found
          </Heading>
          <Text fontSize="sm" color="gray.500" textAlign="center">
            This channel currently has no assigned tasks.
          </Text>
        </Flex>
      ) : (
        <>
          <Flex direction={{ base: "column", md: "row" }} gap={4} align="start">
            {/* Left Section - Task Groups Sidebar */}
            <VStack
              align="start"
              spacing={3}
              w={{ base: "100%", md: "350px" }}
              overflowY="auto"
              bg={sidebarBg}
              borderRadius="lg"
              py={4}
              px={5}
              border="1px solid"
              borderColor={sidebarBorderColor}
              height={`${getTaskListHeight()} px`}
            >
              <HStack w="100%" justify="space-between" px={2}>
                <Text fontWeight="bold" fontSize="sm" color={color}>
                  Tasks Group
                </Text>
                <Badge
                  color={badgeGoldColor}
                  bg={badgeGoldBg}
                  border="1px solid"
                  borderColor={badgeGoldBorder}
                  fontSize="xs"
                  borderRadius={"5px"}
                  px={3}
                >
                  {tasks.length} Tasks group
                </Badge>
              </HStack>

              {/* Search Bar */}
              <InputGroup size="sm" px={2}>
                <InputLeftElement pointerEvents="none" pl={2}>
                  <IconWrapper
                    icon={FaSearch}
                    className="text-gray-400"
                    size={12}
                  />
                </InputLeftElement>
                <Input
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  borderRadius="md"
                  bg={searchInputBg}
                  borderColor={searchInputBorder}
                  _hover={{ borderColor: searchInputHoverBorder }}
                  _focus={{
                    borderColor: "#B59143",
                    boxShadow: "0 0 0 1px #B59143",
                  }}
                />
              </InputGroup>

              {loading ? (
                <Center w="100%" h="200px">
                  <Spinner color="purple.500" />
                </Center>
              ) : filteredTasks.length === 0 ? (
                <Box
                  w="100%"
                  textAlign="center"
                  py={10}
                  color="gray.500"
                  fontStyle="italic"
                  fontSize="sm"
                  bg="gray.50"
                  borderRadius="md"
                >
                  {searchQuery
                    ? "No tasks found matching your search."
                    : "No tasks found in this channel."}
                </Box>
              ) : (
                <VStack spacing={2} w="100%" flex="1">
                  {filteredTasks.map((task) => {
                    const isSelected = selectedTask?.id === task.id;
                    const taskCount = task.data?.length || 0;
                    const subtaskCount =
                      task.data?.reduce(
                        (sum, d) => sum + (d.subtasks?.length || 0),
                        0
                      ) || 0;

                    return (
                      <Box
                        key={task.id}
                        w="100%"
                        px={4}
                        py={3}
                        bg={isDark ? (isSelected ? "#1A1C1F" : "#14161B") : "white"}
                        border="1px solid"
                        borderColor={isDark ? (isSelected ? "#B59143" : "rgba(255,255,255,0.18)") : "#E8C16C"}
                        borderRadius="lg"
                        cursor="pointer"
                        transition="all 0.2s"
                        boxShadow={isSelected ? "md" : "sm"}
                        _hover={{
                          bg: taskItemHoverBg,
                          transform: "translateY(-2px)",
                          borderColor: taskItemHoverBorder,
                        }}
                        onClick={() => setSelectedTask(task)}
                      >
                        <VStack align="start" spacing={2}>
                          <HStack justify="space-between" w="100%">
                            <Text
                              color={color}
                              fontWeight="semibold"
                              fontSize="sm"
                              noOfLines={1}
                            >
                              {task.title}
                            </Text>
                            {isSelected && (
                              <Badge
                                variant="solid"
                                color="#B59143"
                                bg="transparent"
                                border="1px solid"
                                borderColor="#B59143"
                                fontSize="0.6rem"
                                borderRadius="full"
                                px={2}
                                py={0.5}
                              >
                                Selected
                              </Badge>
                            )}
                          </HStack>

                          <HStack spacing={4} fontSize="xs" color={taskItemSubTextColor}>
                            <HStack spacing={1}>
                              <IconWrapper
                                icon={FaFolder}
                                className="text-gray-400"
                                size={12}
                              />
                              <Text>{taskCount} Tasks</Text>
                            </HStack>
                            <HStack spacing={1}>
                              <IconWrapper
                                icon={FaChartBar}
                                className="text-gray-400"
                                size={12}
                              />
                              <Text>{subtaskCount} Subtasks</Text>
                            </HStack>
                          </HStack>
                        </VStack>
                      </Box>
                    );
                  })}
                </VStack>
              )}

            </VStack>

            {/* Right Section - Risk Management Chart */}
            <Box flex="1" p={5}>
              {!selectedTask ? (
                /* Channel-level Overview (when no task is selected) */
                <VStack spacing={4} align="stretch" h="100%">
                  {/* Total Tasks Count Section */}
                  <Box
                    px={4}
                    py={6}
                    bg={summaryBoxBg}
                    mt={-5}
                    borderRadius="md"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor={summaryBoxBorder}
                  >
                    <Text fontWeight="bold" fontSize="md" color={color}>
                      Total Tasks in Channel - {channelName}:{" "}
                      {tasks.reduce((total, task) => {
                        return (
                          total +
                          (task.data?.length || 0) +
                          (task.data?.reduce(
                            (sum, d) => sum + (d.subtasks?.length || 0),
                            0
                          ) || 0)
                        );
                      }, 0)}
                    </Text>
                  </Box>

                  {/* Task Progress Chart */}
                  <Box
                    flex="1"
                    p={4}
                    bg={bg}
                    mt={1}
                    borderRadius="md"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor={boxBorderColor}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <HStack spacing={2} mb={4} justify="center" w="100%">
                      <IconWrapper
                        icon={FaInfoCircle}
                        className={iconColor}
                        size={16}
                      />
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color={chartTextColor}
                        textAlign="center"
                      >
                        Task Progress
                      </Text>
                    </HStack>
                    <Box height="346px" width="100%">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={progressChartData.filter(
                              (item) => item.value > 0
                            )}
                            cx="50%"
                            cy="50%"
                            fontSize={"12px"}
                            outerRadius={75}
                            dataKey="value"
                            label={({ value }) => (value > 0 ? `${value} ` : "")}
                            labelLine={false}
                          >
                            {progressChartData
                              .filter((item) => item.value > 0)
                              .map((entry, index) => {
                                const colors = [
                                  "#CBD5E0",
                                  "#63B3ED",
                                  "#68D391",
                                  "#F6AD55",
                                ];
                                const originalIndex =
                                  progressChartData.findIndex(
                                    (item) => item.name === entry.name
                                  );
                                return (
                                  <Cell
                                    key={`cell - progress - ${index} `}
                                    fill={colors[originalIndex] || "#CBD5E0"}
                                  />
                                );
                              })}
                          </Pie>
                          <Legend
                            verticalAlign="bottom"
                            height={30}
                            wrapperStyle={{
                              fontSize: "11px",
                              paddingTop: "10px",
                            }}
                          />
                          <RechartsTooltip
                            formatter={(value, name) => [value, name]}
                            labelFormatter={(label) => `Status: ${label} `}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </VStack>
              ) : (
                /* Selected Task Details (when a task is selected) */
                <VStack spacing={6} align="stretch">
                  {/* Selected Task Header */}
                  <Box
                    p={5}
                    bg={summaryBoxBg}
                    borderRadius="lg"
                    boxShadow="sm"
                    mt={-5}
                    border="1px solid"
                    borderColor={summaryBoxBorder}
                  >
                    <VStack align="start" spacing={1} mb={1}>
                      <HStack justify="space-between" w="100%">
                        <Heading
                          fontSize={{ base: "lg", md: "xl" }}
                          color={color}
                          noOfLines={1}

                        >
                          {selectedTask.title}
                        </Heading>
                        <Box
                          fontSize="sm"
                          fontWeight="bold"
                          color={descTextColor}
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          Total Entries:{" "}
                          {selectedTask.data?.reduce(
                            (sum, entry) =>
                              sum + 1 + (entry.subtasks?.length || 0),
                            0
                          ) || 0}
                        </Box>
                      </HStack>
                      {selectedTask.description && (
                        <Text fontSize="xs" color={descTextColor}>
                          {selectedTask.description}
                        </Text>
                      )}
                    </VStack>

                  </Box>

                  {/* New Section for Selected Task - height matching Task group list */}
                  <StrikeUser
                    color={color}
                    getNewSectionHeight={getNewSectionHeight}
                    users={users}
                    entries={(selectedTask?.data || []).flatMap((e) => [
                      {
                        id: e.id,
                        name: e.name,
                        assignedUsers: e.assignedUsers || [],
                        status: e.status,
                        plannedStart: (e as any).plannedStart,
                        plannedEnd: (e as any).plannedEnd,
                      },
                      ...(e.subtasks || []).map((s) => ({
                        id: s.id,
                        name: `${e.name} / ${s.name}`,
                        assignedUsers: s.assignedUsers || [],
                        status: s.status,
                        plannedStart: (s as any).plannedStart,
                        plannedEnd: (s as any).plannedEnd,
                      })),
                    ])}
                    organizationId={organizationId}
                    projectId={projectId}
                    channelId={channelId}
                    taskId={selectedTask?.id || ""}
                    isAdmin={isAdmin}
                  />
                </VStack >
              )}
            </Box >
          </Flex >

          {/* Risk Management and Task Status Distribution Section - Full Width Below Task List and New Section */}
          {
            selectedTask && (
              <Box
                w="100%"
                mt={3}
                p={2}
                bg={bg}
                borderRadius="lg"


              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap={8}
                  justify="space-between"
                >
                  {/* LEFT SIDE: RISK MANAGEMENT PIE */}
                  <Box
                    flex="1"
                    border="1px solid"
                    borderColor={boxBorderColor}
                    borderRadius="md"
                    p={4}
                    bg={cardBgCommon}
                    boxShadow="sm"
                  >
                    <Text
                      fontWeight="semibold"
                      mb={4}
                      fontSize="md"
                      color={color}
                      textAlign="center"
                    >
                      Risk Management
                    </Text>
                    <Box height="320px" width="100%">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getSelectedTaskRiskData().filter(
                              (item) => item.value > 0
                            )}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="40%"
                            outerRadius={70}
                            label={({ value }) => (value > 0 ? `${value}` : "")}
                            labelLine={false}
                          >
                            {getSelectedTaskRiskData()
                              .filter((item) => item.value > 0)
                              .map((entry, idx) => {
                                const RISK_COLORS: Record<string, string> = {
                                  High: "#E53E3E",
                                  Medium: "#DD6B20",
                                  Low: "#38A169",
                                };
                                return (
                                  <Cell
                                    key={`risk-cell-${idx}`}
                                    fill={RISK_COLORS[entry.name] || "#CBD5E0"}
                                  />
                                );
                              })}
                          </Pie>
                          <Legend
                            verticalAlign="bottom"
                            height={15}
                            wrapperStyle={legendUp}
                          />
                          <RechartsTooltip
                            formatter={(value, name) => [value, name]}
                            labelFormatter={(label) => `Risk Level: ${label}`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>

                  {/* RIGHT SIDE: Status Chart */}
                  <Box
                    flex="1"
                    border="1px solid"
                    borderColor={boxBorderColor}
                    borderRadius="md"
                    p={4}
                    bg={cardBgCommon}
                    boxShadow="sm"
                  >
                    <Text
                      fontWeight="semibold"
                      mb={4}
                      fontSize="md"
                      color={color}
                      textAlign="center"
                    >
                      Task Status Distribution
                    </Text>
                    <Box height="320px" width="100%">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={selectedStatusData.filter(
                              (item) => item.value > 0
                            )}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="40%"
                            innerRadius={40}
                            outerRadius={70}
                            label={({ value }) => (value > 0 ? `${value}` : "")}
                            labelLine={false}
                          >
                            {selectedStatusData
                              .filter((item) => item.value > 0)
                              .map((entry, idx) => {
                                return (
                                  <Cell
                                    key={`cell-${idx}`}
                                    fill={COLORS[entry.name]}
                                  />
                                );
                              })}
                          </Pie>
                          <Legend
                            verticalAlign="bottom"
                            height={15}
                            wrapperStyle={legendUp}
                          />
                          <RechartsTooltip
                            formatter={(value, name) => [value, name]}
                            labelFormatter={(label) => `Status: ${label}`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Flex>
              </Box>
            )
          }

          {/* Task Progress and Assignment Breakdown Section - Full Width Below Task List and Risk Management */}
          {
            !selectedTask && (
              <Box w="100%" mt={3} p={5} bg={bg} borderRadius="lg" boxShadow="sm">
                {/* Charts Section */}
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap={5}
                  px={0}
                  pb={0}
                >
                  {/* LEFT – Channel Risk Management Chart */}
                  <Box
                    flex="1"
                    minH="260px"
                    border="1px solid"
                    borderColor={boxBorderColor}
                    borderRadius="lg"
                    p={4}
                    bg={cardBgCommon}
                  >
                    <Text
                      fontWeight="semibold"
                      color={"gray.400"}
                      fontSize="sm"
                      mb={2}
                      textAlign="center"
                    >
                      Channel Risk Management
                    </Text>
                    <Box height="252px" width="100%" mt={8}>
                      <RiskManagementChart
                        lowRiskTasks={lowRisk}
                        mediumRiskTasks={mediumRisk}
                        highRiskTasks={highRisk}
                        totalTasks={tasks.length}
                        mode="channel"
                      />
                    </Box>
                  </Box>

                  {/* RIGHT – Assignment Donut Chart */}
                  <Box
                    flex="1"
                    minH="260px"
                    border="1px solid"
                    borderColor={boxBorderColor}
                    borderRadius="lg"
                    p={4}
                    bg={cardBgCommon}
                  >
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      color={"gray.400"}
                      mb={2}
                      textAlign="center"
                    >
                      Assignment Breakdown
                    </Text>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={assignmentChartData.filter(
                            (item) => item.value > 0
                          )}
                          cx="50%"
                          cy="50%"
                          innerRadius={35}
                          outerRadius={75}
                          fontSize={"12px"}
                          dataKey="value"
                          label={({ value }) => (value > 0 ? `${value}` : "")}
                          labelLine={false}
                        >
                          {assignmentChartData
                            .filter((item) => item.value > 0)
                            .map((entry, index) => {
                              const colors = ["#68D391", "#FEB2B2"];
                              const originalIndex = assignmentChartData.findIndex(
                                (item) => item.name === entry.name
                              );
                              return (
                                <Cell
                                  key={`cell-assign-${index}`}
                                  fill={colors[originalIndex] || "#68D391"}
                                />
                              );
                            })}
                        </Pie>
                        <Legend
                          verticalAlign="bottom"
                          height={30}
                          wrapperStyle={{
                            fontSize: "11px",
                            paddingTop: "10px",
                          }}
                        />
                        <RechartsTooltip
                          formatter={(value, name) => [value, name]}
                          labelFormatter={(label) => `Assignment: ${label}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Flex>
              </Box>
            )
          }

          {/* Subtask Details Table (Full Width Below Charts and Task List) */}
          {
            selectedTask && selectedTask.data && (
              <Box
                w={{ base: "100%", md: "auto" }}
                flex="1"
                border="1px solid"
                borderColor={cardBorderColor}
                borderRadius="md"
                overflowX="auto"


                p={5}
                bg={bg}
                mt={6}
              >
                <Flex justify="space-between" mb={4}>
                  <Heading size="sm" color={color}>
                    Task Details
                  </Heading>

                  {/* Entry Search and Filter Controls */}
                  {selectedTask?.data && selectedTask.data.length > 0 && (
                    <HStack spacing={4} mb={4}>
                      <InputGroup size="sm" w={{ base: "100%", md: "200px" }}>
                        <InputLeftElement pointerEvents="none">
                          <IconWrapper
                            icon={FaSearch}
                            className="text-[#B59143]"
                            size={12}
                          />
                        </InputLeftElement>
                        <Input
                          placeholder="Search entries..."
                          value={entrySearchQuery}
                          onChange={(e) => setEntrySearchQuery(e.target.value)}
                          borderRadius="md"
                          bg={inputBg}
                          color={inputTextColor}
                          borderColor="#333"
                          _hover={{ borderColor: "#B59143" }}
                          _focus={{ borderColor: "#B59143", boxShadow: "0 0 0 1px #B59143" }}
                        />
                      </InputGroup>

                      <CustomDropdown
                        value={entryStatusFilter}
                        onChange={(value) => setEntryStatusFilter(value as StatusKey | "All")}
                        isDark={isDark}
                        options={[
                          { value: "All", label: "All Statuses" },
                          { value: "Not-started", label: "Not Started" },
                          { value: "In-progress", label: "In Progress" },
                          { value: "completed", label: "Completed" },
                          { value: "delay", label: "Delay" }
                        ]}
                        className="w-36 px-2 py-1 text-sm rounded-md border"
                        style={{ height: "32px", width: "150px", minWidth: "120px" }}
                      />
                    </HStack>
                  )}
                </Flex>

                <Box maxH="70vh" overflowY="auto">
                  <Table
                    variant="simple"
                    size="sm"
                    borderColor={cardBorderColor}
                    borderWidth="1px"
                    width="100%"
                  >
                    <Thead
                      bg={tableHeaderBg}
                      width="100%"
                      style={{ position: "sticky", top: 0, zIndex: 1 }}
                    >
                      <Tr>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          WBS
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Name
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Planned Start
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Planned End
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Actual Start
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Actual End
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Status
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Risk
                        </Th>
                        <Th
                          textAlign="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          py={3}
                          px={4}
                        >
                          Assignees
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {/* Iterate over original selectedTask data to maintain parent-subtask structure */}
                      {selectedTask?.data?.map((entry) => {
                        // Filter subtasks for the current entry based on search/filter
                        const filteredSubtasks = Array.isArray(entry.subtasks)
                          ? entry.subtasks.filter((sub) => {
                            const subMatchesSearch =
                              sub.name
                                .toLowerCase()
                                .includes(entrySearchQuery.toLowerCase()) ||
                              entrySearchQuery === "";
                            const subMatchesStatus =
                              entryStatusFilter === "All" ||
                              normalizeStatus(sub.status) === entryStatusFilter;
                            return subMatchesSearch && subMatchesStatus;
                          })
                          : [];

                        // Determine if the parent entry should be shown (matches filter itself OR has matching subtasks)
                        const showParent =
                          (entry.name
                            .toLowerCase()
                            .includes(entrySearchQuery.toLowerCase()) &&
                            (entryStatusFilter === "All" ||
                              normalizeStatus(entry.status) ===
                              entryStatusFilter)) ||
                          filteredSubtasks.length > 0;

                        // Only render the fragment if the parent or any subtask matches the filter
                        if (!showParent && filteredSubtasks.length === 0)
                          return null;

                        return (
                          <React.Fragment key={entry.id}>
                            {/* Render parent entry if it should be shown */}
                            {showParent && (
                              <Tr bg={modalBg} _hover={{ bg: listHoverBg }}>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {(entry as any).wbs ?? "—"}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {entry.name}
                                </Td>
                                {/* Added Planned Start/End and Risk */}
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(entry.plannedStart)}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(entry.plannedEnd)}
                                </Td>

                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(entry.actualStart || "")}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(entry.actualEnd || "")}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {entry.status}
                                </Td>
                                {/* Added Risk column for parent */}

                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid" // Add bottom border to the last column
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {entry.risk || "—"}{" "}
                                  {/* Show risk here as well for parent */}
                                </Td>

                                <Td
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      onClick={() => {
                                        const users = entry.assignedUsers || [];
                                        setAssigneeList(users);
                                        onOpen();
                                      }}
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition"
                                      aria-label="View assignees"
                                    >
                                      <IconWrapper
                                        icon={FaUsers}
                                        className="text-gray-600"
                                        size={16}
                                      />
                                    </button>
                                  </div>
                                </Td>
                              </Tr>
                            )}

                            {/* Render filtered subtasks for the current parent entry */}
                            {filteredSubtasks.map((sub) => (
                              <Tr
                                key={sub.id}
                                bg={modalBg}
                                _hover={{ bg: listHoverBg }}
                              >
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {(sub as any).wbs ?? "—"}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  pl={8}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {sub.name}
                                </Td>
                                {/* Added Planned Start/End and Risk for subtasks */}
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(sub.plannedStart)}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(sub.plannedEnd)}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(sub.actualStart || "")}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {formatDateDDMMYYYY(sub.actualEnd || "")}
                                </Td>
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {sub.status}
                                </Td>
                                {/* Added Risk column for subtask */}
                                <Td
                                  color={modalTextColor}
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  {sub.risk || "—"}
                                </Td>
                                <Td
                                  textAlign="center"
                                  borderBottom="1px solid"
                                  borderColor={modalDividerColor}
                                  py={3}
                                  px={4}
                                >
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      onClick={() => {
                                        const users = sub.assignedUsers || [];
                                        setAssigneeList(users);
                                        onOpen();
                                      }}
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition"
                                      aria-label="View assignees"
                                    >
                                      <IconWrapper
                                        icon={FaUsers}
                                        className="text-gray-600"
                                        size={16}
                                      />
                                    </button>
                                  </div>
                                </Td>
                              </Tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )
          }

          {/* Assignees Modal */}
          <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent bg={modalBg} border="1px solid" borderColor={modalBorderColor}>
              <ModalHeader color={color} className="text-md">
                Assigned Users
              </ModalHeader>
              <ModalCloseButton color={modalCloseColor} />
              <ModalBody>
                {getAssigneeNames(assigneeList).length === 0 ? (
                  <div className={gray500 + " italic py-4 text-center"}>
                    No users assigned
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto pr-1">
                    <ul className={"divide-y " + divideColor}>
                      {getAssigneeNames(assigneeList).map((name, idx) => (
                        <li key={idx} className="py-2 flex items-center gap-3">
                          <div className="w-8 h-8 text-xs rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold">
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm" style={{ color: modalTextColor }}>{name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box >
  );
};

export default ChannelDetails;
